import React, { useEffect, useState } from 'react'
import SideBar from '../components/SideBar'
import DetailsHeaderBar from '../components/DetailsHeaderBar'
import { useNavigate } from 'react-router-dom';
import Select from "react-select";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { categoryService } from '../services/categoryService';

function CreateCategory() {
    const navigate = useNavigate();

    const [step, setStep] = useState(1);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [loading, setLoading] = useState(false);
    const [images, setImages] = useState([]);
    const [previewImage, setPreviewImage] = useState(null);

    const [categoryName, setCategoryName] = useState("");
    const [slug, setSlug] = useState("");

    const statusOptions = [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
    ];

    const [status, setStatus] = useState(statusOptions[0]);
    const [description, setDescription] = useState("");

    const validateBasicDetails = () => {
      let valid = true;

      if (!categoryName.trim()) {
        alert("Category name is required");
        valid = false;
      }

      return valid;
    };

    // Step Validation
    const validateStep = () => {
      if (step === 1) {
        return true; // Image is optional
      }
      if (step === 2) {
        return categoryName.trim() !== "";
      }
      return true;
    };

    const nextStep = () => {
      if (step === 2) {
        const isValid = validateBasicDetails();
        if (!isValid) return;
      }
      if (validateStep()) setStep((prev) => Math.min(prev + 1, 5));
      else alert("Please fill all required fields before proceeding.");
    };
    
    const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

    const steps = [
      { label: "Category image", sub: "Enter category image details" },
      { label: "Basic details", sub: "Enter category basic details" },
    ];

    const handleCreateCategory = async () => {
      if (!validateStep()) {
        alert("Please fill all required fields before creating the category.");
        return;
      }

      setLoading(true);
      try {
        const categoryData = {
          name: categoryName,
          slug: (slug || categoryName).toString().toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''),
          description: description,
          status: status.value,
        };

        // Add image if exists
        if (images.length > 0 && images[0].file) {
          categoryData.image = images[0].file;
        }

        const response = await categoryService.createCategory(categoryData);
        
        setOpenSnackbar(true);
        setTimeout(() => {
          navigate("/manage-category");
        }, 2000);
        
      } catch (error) {
        console.error('Create category error:', error);
        alert(error.response?.data?.message || 'Failed to create category');
      } finally {
        setLoading(false);
      }
    };

    const handleImagesChange = (e) => {
        const files = Array.from(e.target.files);

        const newImages = files.map((file) => ({
            file,
            preview: URL.createObjectURL(file),
        }));

        setImages((prev) => [...prev, ...newImages]);
    };

    const handleSnackbarClose = (event, reason) => {
      if (reason === 'clickaway') return;
      setOpenSnackbar(false);
    };

    return (
        <div className="manage-member-layout">
            <SideBar />
            <div className="product-details-main">
                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={3000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <MuiAlert
                        severity="success"
                        icon={<span style={{ fontWeight: "bold", fontSize: "18px", color:'rgba(51, 178, 51, 1' }}>✔</span>}
                        sx={{
                            backgroundColor: "transparent",
                            backdropFilter: "blur(8px)",
                            color: "#000",
                            border: "2px solid rgba(51, 178, 51, 1)",
                            borderRadius: "10px",
                            boxShadow: "none",
                            fontSize: "15px",
                            padding: "8px 12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            height: '25px',
                            width: '100%',
                            textAlign: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        Category created successfully!
                    </MuiAlert>
                </Snackbar>

                <DetailsHeaderBar
                    title="Create New Category"
                    subtitle="After creating a new category, you can manage it."
                    breadcrumbs={[
                        {label: "Home", path: "/manage-member"},
                        {label: "Manage Category", path: "/manage-category"},
                        {label: "Create New Category"}
                    ]}
                />

                <div className="multi-form-container">
                    <div className="progress-bar">
                        {steps.map((item, i) => {
                            const stepNum = i + 1;
                            const isActive = step === stepNum;
                            const isCompleted = step > stepNum;
                            return (
                                <div key={i} className="progress-step">
                                    <div
                                        className={`circle ${isCompleted ? "completed" : isActive ? "active" : ""}`}
                                    >
                                        {isCompleted ? "✓" : stepNum}
                                    </div>
                                    <div className="step-text">
                                        <p className={`step-label ${isActive ? "active-label" : ""}`}>
                                            {item.label}
                                        </p>
                                        <p className={`step-subtext ${isActive ? "active-sub" : ""}`}>
                                            {item.sub}
                                        </p>
                                    </div>
                                    {i < steps.length - 1 && (
                                        <div
                                            className={`line ${isCompleted ? "line-filled" : "line-empty"}`}
                                        ></div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Step content */}
                    <div className="step-content">
                        {step === 1 && (
                            <div>
                                <h3>Category Image Details</h3>
                                <hr/>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Category Image <span className="required"></span></label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImagesChange}
                                            style={{maxWidth:"100%"}}
                                            disabled={loading}
                                        />

                                        <div style={{ display: "flex", gap: 10, marginTop: 10}}>
                                            {images.map((img, index) => (
                                                <div 
                                                    key={index}
                                                    style={{
                                                        position: "relative",
                                                        height: 120,
                                                    }}
                                                >
                                                    <img 
                                                        src={img.preview} 
                                                        alt='category' 
                                                        onClick={() => setPreviewImage(img.preview)}
                                                        style={{
                                                            width: 120,
                                                            height: 120,
                                                            objectFit: "cover",
                                                            borderRadius: 6,
                                                            display: "block",
                                                            marginBottom: 6,
                                                            cursor:"pointer",
                                                        }} 
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setImages(images.filter((_, i) => i !== index))}
                                                        style={{
                                                            position: "absolute",
                                                            top: 4,
                                                            right: 4,
                                                            background: "#e53935",
                                                            color: "#fff",
                                                            border: "none",
                                                            borderRadius: "50%",
                                                            width: 24,
                                                            height: 24,
                                                            cursor: "pointer",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontWeight: "bold",
                                                            fontSize: 14,
                                                        }}
                                                    >
                                                        x
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>       
                        )}
                        {step === 2 && (
                            <div>
                                <h3>Basic Details</h3>
                                <hr/>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>
                                            Category name <span className="required">*</span>
                                        </label>
                                        <input 
                                            type="text" 
                                            value={categoryName}
                                            onChange={(e) => setCategoryName(e.target.value)}
                                            placeholder="Enter category name" 
                                            disabled={loading}
                                         maxLength={255} />
                                    </div>

                                    <div className="form-group">
                                        <label>
                                            Category slug <span className="required">*</span>
                                        </label>
                                        <input 
                                            type="text" 
                                            value={slug}
                                            onChange={(e) => setSlug(e.target.value)}
                                            placeholder="Enter category slug" 
                                            disabled={loading}
                                         maxLength={255} />
                                    </div>

                                    <div className="form-group">
                                        <label>Status <span className="required">*</span></label>
                                        <Select
                                            options={statusOptions}
                                            value={status}
                                            onChange={setStatus}
                                            placeholder={statusOptions[0].label}
                                            isSearchable
                                            classNamePrefix="custom-select"
                                            isDisabled={loading}
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    border: '1.17px solid rgba(209, 209, 209, 1)',
                                                    borderRadius: '17.6px',
                                                    background: 'rgba(253, 253, 253, 1)',
                                                    minHeight: '38px',
                                                    boxShadow: state.isFocused ? 'none' : 'none',
                                                    '&:hover': 'none',
                                                    fontSize: '14px',
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    padding: '0 8px',
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color:'black',
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    backgroundColor: '#fff',
                                                    borderRadius: '6px',
                                                    marginTop: '2px',
                                                    zIndex: 9999,
                                                }),
                                                option: (base, { isSelected, isFocused }) => ({
                                                    ...base,
                                                    backgroundColor: isSelected
                                                        ? '#e6f0ff'
                                                        : isFocused
                                                        ? '#e6f0ff'
                                                        : '#fff',
                                                        color: isSelected ? '#000' : '#000',
                                                        cursor: 'pointer',
                                                }),
                                            }}
                                        />
                                    </div>
                                    <div className="form-group" style={{gridColumn: 'span 2'}}>
                                        <label>
                                            Description <span className="required"></span>
                                        </label>
                                        <textarea
                                            className="note-textarea"
                                            rows="3"
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            placeholder="enter description here"
                                            style={{padding:"10px"}}
                                            disabled={loading}
                                         maxLength={65535} />  
                                    </div>
                                </div>            
                            </div>        
                        )}
                    </div>

                    {/* Navigation Buttons */}
                    <div className="btn-row">
                        {step > 1 && (
                            <button className="btn prev" onClick={prevStep} disabled={loading}>
                                Previous
                            </button>
                        )}
                        {step < 2 && (
                            <button 
                                className="btn next" 
                                onClick={nextStep}
                                disabled={!validateStep() || loading}
                                style={{
                                    opacity: validateStep() ? 1 : 0.6,
                                    cursor: validateStep() ? 'pointer' : 'not-allowed'
                                }}
                            >
                                Next
                            </button>
                        )}
                        {step === 2 && (
                            <button 
                                className="btn next" 
                                onClick={handleCreateCategory}
                                disabled={!validateStep() || loading}
                                style={{
                                    opacity: validateStep() ? 1 : 0.6,
                                    cursor: validateStep() ? 'pointer' : 'not-allowed'
                                }}
                            >
                                {loading ? 'Creating...' : 'Create Category'}
                            </button>
                        )}
                    </div>
                
                    {previewImage && (
                        <div
                            onClick={() => setPreviewImage(null)}
                            style={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                width: "100vw",
                                height: "100vh",
                                background: "rgba(0,0,0,0.8)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 9999,
                            }}
                        >
                            <div style={{ position: "relative" }}>
                                <img
                                    src={previewImage}
                                    alt="Preview"
                                    style={{
                                        maxWidth: "90vw",
                                        maxHeight: "90vh",
                                        borderRadius: 10,
                                    }}
                                />
                                <button
                                    onClick={() => setPreviewImage(null)}
                                    style={{
                                        position: "absolute",
                                        top: -10,
                                        right: -10,
                                        background: "#e53935",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: "50%",
                                        width: 30,
                                        height: 30,
                                        cursor: "pointer",
                                        fontSize: 16,
                                        fontWeight: "bold",
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    )}
                </div>  
            </div>
        </div>
    )
}

export default CreateCategory